import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../config/api';

function fmtDate(s) {
  if (!s) return '—';
  return s.replace('T', ' ').slice(0, 19);
}

const AdminPanel = ({ onClose }) => {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [busyMac, setBusyMac] = useState(null);
  const [busyUserId, setBusyUserId] = useState(null);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const res = await fetch(`${API_BASE}/devices/admin_list_devices.php`, { credentials: 'include' });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Erreur');
      setDevices(json.devices || []);
    } catch (e) {
      setListError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (unlocked) fetchDevices();
  }, [unlocked, fetchDevices]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      const res = await fetch(`${API_BASE}/devices/admin_verify_password.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password })
      });
      const json = await res.json();
      if (!json.ok) throw new Error('Mot de passe incorrect');
      setUnlocked(true);
    } catch (e) {
      setAuthError(e.message || 'Erreur');
    } finally {
      setAuthLoading(false);
    }
  };

  const callFreezeAction = async (userId, frozen, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusyUserId(userId);
    try {
      const url = `${API_BASE}/user/admin_set_account_frozen.php?user_id=${userId}&frozen=${frozen ? 1 : 0}`;
      const res = await fetch(url, { credentials: 'include' });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Erreur');
      await fetchDevices();
    } catch (e) {
      alert('Erreur : ' + (e.message || e));
    } finally {
      setBusyUserId(null);
    }
  };

  const callAction = async (mac, endpoint, confirmMsg, extraParams = '') => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusyMac(mac);
    try {
      const url = `${API_BASE}/devices/${endpoint}?identifier=${mac}${extraParams}`;
      const res = await fetch(url, { credentials: 'include' });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Erreur');
      await fetchDevices();
    } catch (e) {
      alert('Erreur : ' + (e.message || e));
    } finally {
      setBusyMac(null);
    }
  };

  return (
    <div className="overlay overlay--center admin-overlay" onClick={onClose}>
      <div className={`card admin-panel-card ${unlocked ? 'admin-panel-card--unlocked' : 'admin-panel-card--locked'}`} onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} title="Fermer" aria-label="Fermer">×</button>
        <h2 style={{ margin: '0 0 14px', fontSize: 19, color: '#0d3a57' }}>🔐 Panneau admin</h2>

        {!unlocked ? (
          <form onSubmit={submitPassword}>
            <p style={{ fontSize: 14, color: '#5b6470', marginBottom: 10 }}>
              Mot de passe admin dédié (distinct de votre mot de passe de connexion).
            </p>
            <input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => { setAuthError(''); setPassword(e.target.value); }}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #ccc', fontSize: 15, boxSizing: 'border-box' }}
              placeholder="Mot de passe admin"
            />
            {authError && <div className="error-text" style={{ marginTop: 8 }}>{authError}</div>}
            <div style={{ marginTop: 18, display: 'flex', gap: 10, justifyContent: 'space-between' }}>
              <button type="button" onClick={onClose}
                style={{ padding: '9px 18px', background: '#eee', color: '#333', fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer' }}
              >❌ Annuler</button>
              <button type="submit" disabled={authLoading || !password}
                style={{ padding: '9px 20px', background: '#0b8a4f', color: '#fff', fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer' }}
              >{authLoading ? '…' : 'Déverrouiller'}</button>
            </div>
          </form>
        ) : (
          <div>
            {loading && <p>Chargement…</p>}
            {listError && <div className="error-text">{listError}</div>}
            {!loading && !listError && (
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '2px solid #e0e4e8' }}>
                      <th style={{ padding: '6px 8px' }}>Nom</th>
                      <th style={{ padding: '6px 8px' }}>Propriétaire</th>
                      <th style={{ padding: '6px 8px' }}>État</th>
                      <th style={{ padding: '6px 8px' }}>Dernière confirmation</th>
                      <th style={{ padding: '6px 8px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map(d => {
                      const isBusy = busyMac === d.identifier;
                      const active = !!d.device_id;
                      const label = `${d.device_name || d.name || d.identifier} (${d.identifier})`;
                      return (
                        <tr key={d.identifier}>
                          <td data-label="Nom"><div className="admin-cell-value">{d.device_name || d.name || d.identifier}</div></td>
                          <td data-label="Propriétaire">
                            <div className="admin-cell-value">
                              {d.owner_email || d.owner_username || `user_id ${d.registry_user_id}`}
                              {d.removed_at && <div style={{ fontSize: 11, color: '#b45' }}>supprimé le {fmtDate(d.removed_at)}</div>}
                              {d.owner_frozen_at && <div style={{ fontSize: 11, color: '#0969da', fontWeight: 700 }}>🧊 compte gelé le {fmtDate(d.owner_frozen_at)}</div>}
                              {d.registry_user_id && (
                                <button
                                  className="btn btn-light admin-btn" disabled={busyUserId === d.registry_user_id}
                                  title={d.owner_frozen_at
                                    ? "Réautorise toutes les actions du compte propriétaire (ON/OFF, plannings, mot de passe, invitations, appareils)"
                                    : "Bloque TOUTES les actions du compte propriétaire (ON/OFF, plannings, mot de passe, invitations, appareils) jusqu'au dégel — pas seulement cet appareil"}
                                  onClick={() => callFreezeAction(
                                    d.registry_user_id,
                                    !d.owner_frozen_at,
                                    d.owner_frozen_at
                                      ? `Dégeler le compte de ${d.owner_email || d.registry_user_id} ?`
                                      : `Geler le compte de ${d.owner_email || d.registry_user_id} ? Toutes les fonctions du compte seront bloquées (ajout/suppression d'appareil, ON/OFF, plannings, mot de passe, invitations...) jusqu'a ce que vous le degeliez.`
                                  )}
                                >{d.owner_frozen_at ? '🧊 Dégeler le compte' : '🧊 Geler le compte'}</button>
                              )}
                            </div>
                          </td>
                          <td data-label="État">
                            <div className="admin-cell-value">
                              {active
                                ? (d.online ? '🟢 en ligne' : '⚪ hors ligne') + (String(d.enabled) === '0' ? ' — désactivé' : '')
                                : '— (pas dans devices)'}
                            </div>
                          </td>
                          <td data-label="Dernière confirmation"><div className="admin-cell-value">{fmtDate(d.last_confirmed)}</div></td>
                          <td data-label="Actions" className="admin-actions">
                            <div className="admin-cell-value admin-actions">
                              {active && (
                                <button
                                  className="btn btn-light admin-btn" disabled={isBusy}
                                  title={String(d.enabled) === '0'
                                    ? "Réautorise l'allumage de cet appareil (le reste du compte n'est pas concerné)"
                                    : "Empêche d'allumer CET appareil (kill switch) — le reste du compte continue de fonctionner normalement"}
                                  onClick={() => callAction(
                                    d.identifier,
                                    'set_device_enabled.php',
                                    String(d.enabled) === '0'
                                      ? `Réactiver ${label} ?`
                                      : `Désactiver ${label} (kill switch) ?`,
                                    `&enabled=${String(d.enabled) === '0' ? 1 : 0}`
                                  )}
                                >{String(d.enabled) === '0' ? '▶️ Activer' : '⏸ Désactiver'}</button>
                              )}
                              {active && (
                                <button
                                  className="btn btn-warning admin-btn" disabled={isBusy}
                                  title="Retire l'appareil du compte de son propriétaire — le verrou d'appairage est conservé, il ne pourra pas le réajouter sans le libérer d'abord"
                                  onClick={() => callAction(d.identifier, 'admin_delete_device.php', `Supprimer ${label} du compte de son propriétaire ? (le verrou est conservé)`)}
                                >🗑 Supprimer</button>
                              )}
                              <button
                                className="btn btn-danger admin-btn" disabled={isBusy}
                                title="Réinitialise totalement l'appairage : n'importe quel compte pourra ensuite associer cet appareil"
                                onClick={() => callAction(d.identifier, 'admin_release_device.php', `Libérer totalement ${label} ? N'importe quel compte pourra ensuite l'appairer.`)}
                              >🔓 Libérer</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {devices.length === 0 && (
                      <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#8a929c' }}>Aucun appareil enregistré.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose}
                style={{ padding: '9px 18px', background: '#eee', color: '#333', fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer' }}
              >Fermer</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
